import { useState, useEffect } from 'react';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { X, Cookie, Settings, Check } from 'lucide-react';
import { OPEN_COOKIE_PREFERENCES_EVENT, CMP_UNAVAILABLE_EVENT, syncExternalCmpConsent } from '@/lib/cookiePreferences';
import { trackMetaPageView, revokeMetaPixel } from '@/lib/metaPixel';


const translations = {
  tr: {
    title: 'Çerez Kullanımı',
    description: 'Web sitemizde deneyiminizi geliştirmek, site trafiğini analiz etmek ve pazarlama çalışmalarımızı optimize etmek için çerezler kullanıyoruz.',
    acceptAll: 'Tümünü Kabul Et',
    rejectAll: 'Reddet',
    customize: 'Özelleştir',
    save: 'Kaydet',
    necessary: 'Zorunlu Çerezler',
    necessaryDesc: 'Web sitesinin düzgün çalışması için gereklidir.',
    analytics: 'Analitik Çerezler',
    analyticsDesc: 'Ziyaretçi davranışlarını anlamamıza yardımcı olur.',
    marketing: 'Pazarlama Çerezleri',
    marketingDesc: 'Kişiselleştirilmiş reklamlar göstermek için kullanılır.',
    privacyLink: 'Gizlilik Politikası',
  },
  en: {
    title: 'Cookie Consent',
    description: 'We use cookies to enhance your experience, analyze site traffic, and optimize our marketing efforts.',
    acceptAll: 'Accept All',
    rejectAll: 'Reject All',
    customize: 'Customize',
    save: 'Save Preferences',
    necessary: 'Necessary Cookies',
    necessaryDesc: 'Required for the website to function properly.',
    analytics: 'Analytics Cookies',
    analyticsDesc: 'Help us understand visitor behavior.',
    marketing: 'Marketing Cookies',
    marketingDesc: 'Used to show personalized advertisements.',
    privacyLink: 'Privacy Policy',
  },
  fr: {
    title: 'Consentement aux Cookies',
    description: 'Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic et optimiser nos efforts marketing.',
    acceptAll: 'Tout Accepter',
    rejectAll: 'Tout Refuser',
    customize: 'Personnaliser',
    save: 'Enregistrer',
    necessary: 'Cookies Nécessaires',
    necessaryDesc: 'Requis pour le bon fonctionnement du site.',
    analytics: 'Cookies Analytiques',
    analyticsDesc: 'Nous aident à comprendre le comportement des visiteurs.',
    marketing: 'Cookies Marketing',
    marketingDesc: 'Utilisés pour afficher des publicités personnalisées.',
    privacyLink: 'Politique de Confidentialité',
  },
  nl: {
    title: 'Cookie Toestemming',
    description: 'We gebruiken cookies om uw ervaring te verbeteren, siteverkeer te analyseren en onze marketinginspanningen te optimaliseren.',
    acceptAll: 'Alles Accepteren',
    rejectAll: 'Alles Weigeren',
    customize: 'Aanpassen',
    save: 'Voorkeuren Opslaan',
    necessary: 'Noodzakelijke Cookies',
    necessaryDesc: 'Vereist voor de goede werking van de website.',
    analytics: 'Analytische Cookies',
    analyticsDesc: 'Helpen ons het gedrag van bezoekers te begrijpen.',
    marketing: 'Marketing Cookies',
    marketingDesc: 'Gebruikt om gepersonaliseerde advertenties te tonen.',
    privacyLink: 'Privacybeleid',
  },
  ar: {
    title: 'موافقة ملفات تعريف الارتباط',
    description: 'نستخدم ملفات تعريف الارتباط لتحسين تجربتك وتحليل حركة المرور وتحسين جهودنا التسويقية.',
    acceptAll: 'قبول الكل',
    rejectAll: 'رفض الكل',
    customize: 'تخصيص',
    save: 'حفظ التفضيلات',
    necessary: 'ملفات تعريف الارتباط الضرورية',
    necessaryDesc: 'مطلوبة لعمل الموقع بشكل صحيح.',
    analytics: 'ملفات تعريف الارتباط التحليلية',
    analyticsDesc: 'تساعدنا على فهم سلوك الزوار.',
    marketing: 'ملفات تعريف الارتباط التسويقية',
    marketingDesc: 'تستخدم لعرض إعلانات مخصصة.',
    privacyLink: 'سياسة الخصوصية',
  },
};

interface ConsentState {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [consent, setConsent] = useState<ConsentState>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  // 6 months in milliseconds
  const CONSENT_EXPIRY_MS = 6 * 30 * 24 * 60 * 60 * 1000;

  const evaluateStoredConsent = () => {
    const savedConsent = localStorage.getItem('cookie_consent');
    const consentDate = localStorage.getItem('cookie_consent_date');

    if (!savedConsent || !consentDate) {
      setIsVisible(true);
      return;
    }
    // Check if consent has expired (6 months)
    const consentTimestamp = new Date(consentDate).getTime();
    const isExpired = (Date.now() - consentTimestamp) > CONSENT_EXPIRY_MS;

    if (isExpired) {
      localStorage.removeItem('cookie_consent');
      localStorage.removeItem('cookie_consent_date');
      setIsVisible(true);
    } else {
      const parsed = JSON.parse(savedConsent);
      setConsent(parsed);
      updateGoogleConsent(parsed);
    }
  };

  useEffect(() => {
    // Kullanıcı footer'daki "Gizlilik ayarları" bağlantısını kullandığında
    // (harici CMP yoksa) dahili paneli aç.
    const handleOpenRequest = () => {
      setShowCustomize(true);
      setIsVisible(true);
    };
    // CMP script'i yüklenemediyse dahili banner'a geri dön.
    const handleCmpUnavailable = () => evaluateStoredConsent();

    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, handleOpenRequest);
    window.addEventListener(CMP_UNAVAILABLE_EVENT, handleCmpUnavailable);

    const externalCmpActive = (window as unknown as { __ENABLE_EXTERNAL_CMP?: boolean }).__ENABLE_EXTERNAL_CMP;
    if (externalCmpActive) {
      // Harici CMP aktifse kendi banner'ımızı gösterme (yedek olarak bekle)
      setIsVisible(false);
      syncExternalCmpConsent((marketing, analytics) => {
        const state = { necessary: true, analytics, marketing };
        setConsent(state);
        updateGoogleConsent(state);
        if (marketing) trackMetaPageView();
        else revokeMetaPixel();
      });
    } else {
      evaluateStoredConsent();
    }

    return () => {
      window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, handleOpenRequest);
      window.removeEventListener(CMP_UNAVAILABLE_EVENT, handleCmpUnavailable);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CONSENT_EXPIRY_MS]);


  const updateGoogleConsent = (consentState: ConsentState) => {
    // Update Google Consent Mode
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': consentState.analytics ? 'granted' : 'denied',
        'ad_storage': consentState.marketing ? 'granted' : 'denied',
        'ad_user_data': consentState.marketing ? 'granted' : 'denied',
        'ad_personalization': consentState.marketing ? 'granted' : 'denied',
      });
    }
    
    // Push to dataLayer for GTM triggers
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        'event': 'consent_update',
        'consent_analytics': consentState.analytics ? 'granted' : 'denied',
        'consent_marketing': consentState.marketing ? 'granted' : 'denied',
        'consent_necessary': 'granted'
      });
    }
  };

  const saveConsent = (consentState: ConsentState) => {
    localStorage.setItem('cookie_consent', JSON.stringify(consentState));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    updateGoogleConsent(consentState);
    setIsVisible(false);
    
    // Also push consent given event
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        'event': 'cookie_consent_given',
        'consent_type': consentState.analytics && consentState.marketing ? 'all' : 
                        consentState.analytics ? 'analytics_only' : 
                        consentState.marketing ? 'marketing_only' : 'necessary_only'
      });
    }

    // Dispatch custom event for other components to listen
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('consent_updated', { 
        detail: consentState 
      }));
    }

    // Meta Pixel loads only now, after marketing consent; withdrawal revokes it.
    if (consentState.marketing) {
      trackMetaPageView();
    } else {
      revokeMetaPixel();
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true };
    setConsent(allAccepted);
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = { necessary: true, analytics: false, marketing: false };
    setConsent(onlyNecessary);
    saveConsent(onlyNecessary);
  };

  const handleSaveCustom = () => {
    saveConsent(consent);
    setShowCustomize(false);
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[200] p-2 sm:p-3 md:p-4 pointer-events-none"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="pointer-events-auto max-w-md md:max-w-lg mx-auto md:mx-0 md:ml-4 bg-background/95 backdrop-blur border border-border rounded-xl shadow-lg overflow-hidden">
        <div className="p-3 md:p-4">
          <div className="flex items-start gap-2.5">
            <Cookie className="w-4 h-4 md:w-5 md:h-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm font-semibold text-foreground leading-tight">{t.title}</p>
              <p className="text-[11px] md:text-xs text-muted-foreground mt-1 line-clamp-2">
                {t.description}
              </p>
            </div>
            <button
              onClick={handleRejectAll}
              className="p-1 -m-1 hover:bg-muted rounded-full transition-colors shrink-0"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Customize Panel */}
          {showCustomize && (
            <div className="space-y-2.5 mt-3 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-medium text-foreground">{t.necessary}</p>
                <Check className="w-4 h-4 text-green-500 shrink-0" />
              </div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-medium text-foreground">{t.analytics}</p>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={consent.analytics}
                    onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-medium text-foreground">{t.marketing}</p>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={consent.marketing}
                    onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex items-center gap-2 mt-3">
            {showCustomize ? (
              <Button size="sm" onClick={handleSaveCustom} className="h-8 px-3 text-xs flex-1">
                {t.save}
              </Button>
            ) : (
              <>
                <Button size="sm" onClick={handleAcceptAll} className="h-8 px-3 text-xs flex-1">
                  {t.acceptAll}
                </Button>
                <Button size="sm" onClick={handleRejectAll} variant="outline" className="h-8 px-3 text-xs">
                  {t.rejectAll}
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowCustomize(true)}
                  variant="ghost"
                  className="h-8 w-8 p-0 shrink-0"
                  aria-label={t.customize}
                  title={t.customize}
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>

          <LocalizedLink to="/gdpr-policy" className="mt-2 inline-block text-[10px] text-primary hover:underline">
            {t.privacyLink}
          </LocalizedLink>
        </div>
      </div>
    </div>
  );
};


export default CookieConsent;
